<template>
  <div class="media-tools-container bsp-page">
    <div class="main-content bsp-page__content">
      <BspPageHeader
        :breadcrumbs="mediaBreadcrumbs"
        title="Media tools"
        description="Look up a Meta file by ID and download its content."
        eyebrow="Administrative utilities"
        icon="tabler:photo"
      />

      <div class="bsp-tool-grid">
        <section class="bsp-section media-section" aria-labelledby="media-lookup-title">
          <div class="bsp-section-heading">
            <div>
              <span class="bsp-section-heading__eyebrow">Step 1</span>
              <h2 id="media-lookup-title">Find file</h2>
              <p>Get the temporary URL associated with the identifier from Meta.</p>
            </div>
          </div>
          <AInput
            v-model="mediaID"
            label="File ID"
            placeholder="Enter the media ID"
            prepend-inner-icon="tabler:fingerprint"
            clearable
          />
          <AButton color="blue" prepend-icon="tabler:search" :disabled="!mediaID" @click="getMedia">
            Get URL
          </AButton>
          <div v-if="mediaUrlResponse" class="bsp-response-card">
            <ATag text="URL found" color="success" type="secondary" prepend-icon="tabler:check" />
            <code>{{ mediaUrlResponse.url }}</code>
          </div>
        </section>

        <section class="bsp-section media-section" :class="{ 'bsp-section--disabled': !mediaUrl }" aria-labelledby="media-download-title">
          <div class="bsp-section-heading">
            <div>
              <span class="bsp-section-heading__eyebrow">Step 2</span>
              <h2 id="media-download-title">Download file</h2>
              <p>Download the content using the URL obtained in the previous step.</p>
            </div>
          </div>
          <div class="bsp-response-card" v-if="mediaUrl">
            <span>URL ready to download</span>
            <code>{{ mediaUrl }}</code>
          </div>
          <AEmptyViewCard
            v-else
            icon="tabler:link-off"
            title="Find a file first"
            description="The download will be enabled when a valid URL is available."
          />
          <AButton color="blue" prepend-icon="tabler:download" :disabled="!mediaUrl" @click="downloadMedia">
            Download file
          </AButton>
          <div v-if="DownloadmediaUrlResponse" class="bsp-response-card">
            <ATag text="Download complete" color="success" type="secondary" />
            <code>{{ DownloadmediaUrlResponse }}</code>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import { useRouter, useRoute } from 'vue-router'
import AuthService from '../services/AuthService/AuthService'
import { onMounted, ref } from 'vue'
import { User } from '../services/AuthService/types'
import { Establishment } from '../services/EstablishmentService/types'
// import AccountService from '../services/AccountService/AccountService'
import { POSITION, useToast } from 'vue-toastification'
import EstablishmentService from '@/services/EstablishmentService/EstablishmentService'
import MetaService from '@/services/MetaService/MetaService'
import BspPageHeader from '@/components/BspPageHeader.vue'
import { AButton, AEmptyViewCard, AInput, ATag } from '@adereso-ai/vue-components'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const loading = ref(true)
const currentUser = ref<User | undefined>(undefined)
const currentEstablishment = ref<Establishment | undefined>(undefined)
// const establishmentName = ref('')

const mediaUrlResponse = ref('')
const DownloadmediaUrlResponse = ref('')
const mediaID = ref('')
const mediaUrl = ref('')

const mediaBreadcrumbs = [
  { title: 'WhatsApp Accounts', icon: 'tabler:brand-whatsapp', to: { name: 'whatsapp-business-accounts' } },
  { title: 'Media tools', disabled: true }
]

async function getMedia () {
  if (mediaID.value === '') {
    toastError('Media ID is required')
    return
  }

  const metaService = await MetaService.getInstance()
  try {
    const mediaUrlResp = await metaService.getMediaUrl(mediaID.value)
    mediaUrlResponse.value = mediaUrlResp
    mediaUrl.value = mediaUrlResp.url
  } catch (error) {
    toastError(error as string)
  }
}

async function downloadMedia () {
  if (mediaUrl.value === '') {
    toastError('Media URL is required')
    return
  }

  const metaService = await MetaService.getInstance()
  try {
    const mediaUrlResp = await metaService.downloadMediaUrl(mediaID.value, mediaUrl.value)
    DownloadmediaUrlResponse.value = mediaUrlResp
  } catch (error) {
    toastError(error as string)
  }
}

async function getEstablishment () {
  const establishmentService = await EstablishmentService.getInstance()
  try {
    const establishment = await establishmentService.getCurrentEstablishment()
    currentEstablishment.value = establishment
  } catch (error) {
    // do nothing
  }
}

async function getCurrentUser () {
  const authService = await AuthService.getInstance()
  currentUser.value = authService.currentUser.value
  if (authService.currentUser.value == null) {
    router.push({ name: 'login', query: route.query })
  }
}

onMounted(async () => {
  await router.isReady()

  await getCurrentUser()

  await getEstablishment()

  loading.value = false
})

function toastError (errorMessage: string) {
  try {
    toast.error(String(errorMessage), {
      position: POSITION.BOTTOM_LEFT
    })
  } catch (error) {
    console.log('error on toast:', error)
  }
}

</script>

<style scoped lang="scss">
/* Variables de Color */
$primary-color: #0052CC;
$secondary-color: #6B778C;
$success-color: #36B37E;
$danger-color: #FF5630;
$whatsapp-color: #25D366;
$background-color: #F4F5F7;
$text-color: #172B4D;
$border-color: #DFE1E6;

/* Importar Font Awesome */
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css');

/* Estilos Generales */
.media-tools-container {
  padding: 20px;
  background-color: $background-color;
  color: $text-color;
  height: 100%;

  .btn-back {
      @extend .btn;
      background: none;
      border: none;
      color: $primary-color;
      font-size: 16px;
      cursor: pointer;
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      i {
        margin-right: 5px;
      }

      &:hover {
        text-decoration: underline;
      }
    }

  .main-content {
    max-width: 600px;
    margin: 0 auto;
    background-color: #fff;
    border: 1px solid $border-color;
    border-radius: 8px;
    padding: 30px;

    h2 {
      margin-top: 0;
      font-size: 24px;
      margin-bottom: 20px;
    }

    .media-section {
      margin-bottom: 30px;

      h3 {
        margin-bottom: 15px;
        font-size: 20px;
      }

      .form-group {
        margin-bottom: 15px;

        label {
          display: block;
          margin-bottom: 5px;
          font-weight: bold;
        }

        input {
          width: 100%;
          padding: 10px;
          box-sizing: border-box;
          border: 1px solid $border-color;
          border-radius: 4px;
          font-size: 14px;
        }
      }

      .btn-primary {
        @extend .btn;
        background-color: $primary-color;
        border-color: $primary-color;
        display: flex;
        align-items: center;
        margin-bottom: 15px;

        i {
          margin-right: 5px;
        }
      }

      .response {
        background-color: #F1F3F5;
        padding: 15px;
        border-radius: 6px;
        border: 1px solid $border-color;
        font-size: 14px;
        color: $text-color;

        h4 {
          margin-top: 0;
          margin-bottom: 5px;
        }

        p {
          margin: 0;
          word-break: break-all;
        }
      }

      .media-url-display {
        background-color: #F1F3F5;
        padding: 10px;
        border-radius: 6px;
        border: 1px solid $border-color;
        margin-bottom: 15px;

        p {
          margin: 0;
          font-size: 14px;
          word-break: break-all;
        }
      }
    }
  }
}

/* Estilos para Botones */
.btn {
  display: inline-block;
  font-weight: 500;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  padding: 10px 20px;
  font-size: 14px;
  line-height: 1.5;
  border-radius: 4px;
  border: 1px solid transparent;
  color: #fff;
  background-color: $primary-color;
  border-color: $primary-color;
  text-decoration: none;

  &:hover {
    background-color: darken($primary-color, 5%);
    border-color: darken($primary-color, 5%);
  }

  &.btn-primary {
    background-color: $primary-color;
    border-color: $primary-color;
  }
}
</style>
